import { useEffect, useState } from 'react';

const SCROLL_HYSTERESIS = 6; // px — mikro-ruchy scrolla nie zmieniają stanu
const IGNORE_MS = 600;       // po nawigacji hash pomijamy programowy smooth-scroll

// Widoczność paska: ukryty przy sekcji hero oraz przy scrollowaniu w dół,
// pojawia się przy scrollowaniu w górę (poza hero). Kliknięcie kotwicy
// (np. „Poznaj naszą ofertę" / scroll-indicator) wymusza pokazanie paska;
// pierwszy ręczny przewrót (wheel/touch/klawisze) zwalnia wymuszenie.
export function useNavVisibility(view) {
  const [heroVisible, setHeroVisible] = useState(false);
  const [scrollingDown, setScrollingDown] = useState(false);
  const [forced, setForced] = useState(false);

  useEffect(() => {
    setForced(false);
    setScrollingDown(false); // wejście w podwidok (produkt/galeria) pokazuje pasek

    // Zaraz po zmianie widoku programowy smooth-scroll mógłby „wyłączyć"
    // pasek — ignorujemy więc scroll przez krótką chwilę.
    const ignoreUntil = Date.now() + IGNORE_MS;

    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        if (Date.now() < ignoreUntil) return;
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        if (Math.abs(delta) < SCROLL_HYSTERESIS) return;
        setScrollingDown(delta > 0);
      });
    };

    // Kliknięcie kotwicy (poza #strona) pokazuje pasek: hero-btn i
    // scroll-indicator prowadzą do #sklep, a przy scrollu w dół pasek
    // znikałby — wymuszamy więc widoczność na czas płynnego przewijania.
    // Kliknięcie logo (#strona) robi odwrotnie: kieruje do hero, więc pasek
    // chowamy od razu, nie czekając na koniec scrolla ani na obserwatora.
    const onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest('a[href^="#"]');
      if (!anchor) return;
      if (anchor.getAttribute('href') === '#strona') {
        setHeroVisible(true);
        return;
      }
      setForced(true);
    };

    // Pierwszy realny przewrót użytkownika zwalnia wymuszenie.
    const release = () => setForced(false);

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('click', onClick);
    window.addEventListener('wheel', release, { passive: true });
    window.addEventListener('touchmove', release, { passive: true });
    window.addEventListener('keydown', release);

    const removeListeners = () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('click', onClick);
      window.removeEventListener('wheel', release);
      window.removeEventListener('touchmove', release);
      window.removeEventListener('keydown', release);
    };

    if (view !== 'strona' || typeof IntersectionObserver !== 'function') {
      setHeroVisible(false);
      return removeListeners;
    }

    const hero = document.getElementById('strona');
    if (!hero) {
      setHeroVisible(false);
      return removeListeners;
    }

    // Hero „znika", gdy przestaje wypełniać ~55% ekranu.
    const observer = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { rootMargin: '0px 0px -45% 0px', threshold: 0 }
    );
    observer.observe(hero);
    return () => {
      observer.disconnect();
      removeListeners();
    };
  }, [view]);

  // Ukryty przy hero lub gdy scrollujemy w dół; widoczny przy scrollu w górę
  // lub gdy użytkownik właśnie kliknął kotwicę (wymuszenie).
  return forced || (!heroVisible && !scrollingDown);
}

// Scrollspy: id sekcji aktualnie w środku ekranu. W podwidokach (#galeria
// / #produkt) sekcje nie istnieją – aktywność wynika wprost z `view`.
export function useScrollSpy(ids, view) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (view !== 'strona') {
      setActive(view === 'galeria' ? 'galeria' : 'sklep');
      return undefined;
    }

    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (sections.length === 0) return undefined;
    if (typeof IntersectionObserver !== 'function') {
      setActive(ids[0]);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [ids, view]);

  return active;
}